import { Box } from "@mui/material";
import createStyles from "@mui/styles/createStyles";
import makeStyles from "@mui/styles/makeStyles";
import { prepareIntervalType } from "components/Layout/Campaign/utils/utils";
import { OLTheme, defaultOLTheme } from "contexts/Theme/ThemeProvider";
import { useMessage } from "hooks/intl";
import { SelectedLimitType } from "types/campaigns";
import { LimitValues } from "types/members";
import { formatNumberValue } from "utilities/numbers";
import { BarChartPercentage } from "./BarChartPercentage";

const useStyles = makeStyles((theme: OLTheme) =>
  createStyles({
    currentProgress: {
      display: "flex",
      justifyContent: "flex-end",
    },
    barChart: {
      marginLeft: theme.spacing(4),
      width: "58px",
    },
  }),
);

/**
 *
 * @param isActive Flag indicating the activity of the component
 * @param isLimited Flag indicating whether there is a limitation
 * @param limitsType Type of the selected limitation (e.g., daily, weekly)
 * @param limitUsages Object containing the current and maximum values
 * @returns Structure representing a progress display with conditional rendering based on whether there are limitations
 */
export function ProgressComponent({
  isActive,
  isLimited,
  limitsType,
  limitUsages,
  interval,
}: {
  isActive: boolean;
  isLimited: boolean;
  limitsType?: SelectedLimitType;
  limitUsages?: LimitValues;
  interval?: string;
}) {
  const classes = useStyles();
  const { message } = useMessage();

  // Current value
  const currentValue = limitUsages?.currentValue;
  // Maximum value
  const limitValue = limitUsages?.limitValue;
  // Remaining value, should be equal limitValue - currentValue
  const remainingValue = limitUsages?.remaining;

  // Progress color for the bar chart
  const barChartProgressColor = isActive
    ? defaultOLTheme.primary500
    : defaultOLTheme.gray500;
  // Remaining color for the bar chart
  const barChartRemainingColor = isActive
    ? defaultOLTheme.primary200
    : defaultOLTheme.gray200;

  return (
    <>
      {isLimited ? (
        <span className={classes.currentProgress}>
          <Box sx={{ fontWeight: "bold" }}>
            {formatNumberValue(currentValue, "")}
          </Box>
          {`/${formatNumberValue(limitValue, "")} ${
            interval?.toLowerCase() || ""
          } ${
            limitsType
              ? message({
                  id: prepareIntervalType(limitsType),
                }).toLowerCase()
              : ""
          }`}
          <BarChartPercentage
            barChartData={[
              {
                itemShare: currentValue!,
                itemColor: barChartProgressColor,
              },
              {
                itemShare: remainingValue!,
                itemColor: barChartRemainingColor,
              },
            ]}
            className={classes.barChart}
          />
        </span>
      ) : (
        message({ id: "unlimited" })
      )}
    </>
  );
}
